import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Music, Mail, Lock, ArrowRight, CheckCircle2 } from "lucide-react";

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [signupDone, setSignupDone] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) navigate("/create", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (forgotMode) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setForgotSent(true);
        toast({ title: "Письмо отправлено", description: "Проверьте почту для сброса пароля" });
      } else if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate("/create");
      } else {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        setSignupDone(true);
      }
    } catch (error: any) {
      toast({
        title: "Ошибка",
        description: error.message === "Invalid login credentials"
          ? "Неверный email или пароль"
          : error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4 pt-24 pb-16">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
              <Music className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-display font-bold text-foreground">
              {forgotMode ? "Сброс пароля" : isLogin ? "Войти в аккаунт" : "Создать аккаунт"}
            </h1>
            <p className="text-muted-foreground mt-2">
              {forgotMode
                ? "Введите email и мы отправим ссылку для сброса"
                : isLogin
                  ? "Войдите, чтобы создавать песни и управлять ими"
                  : "Зарегистрируйтесь, чтобы начать создавать хиты"}
            </p>
          </div>

          {/* Signup confirmation screen */}
          {signupDone ? (
            <div className="text-center space-y-5 py-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <div>
                <p className="text-lg font-bold text-foreground">Почти готово!</p>
                <p className="text-muted-foreground mt-2 text-sm">
                  Мы отправили письмо с подтверждением на
                </p>
                <p className="text-foreground font-semibold mt-1">{email}</p>
              </div>
              <div className="bg-secondary/50 rounded-xl p-4 text-left space-y-2">
                <p className="text-sm text-foreground font-medium">Что нужно сделать:</p>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Откройте почту <span className="font-medium text-foreground">{email}</span></li>
                  <li>Найдите письмо от нас (проверьте «Спам»)</li>
                  <li>Нажмите на ссылку подтверждения</li>
                </ol>
              </div>
              <p className="text-xs text-muted-foreground">
                После подтверждения вы сможете войти и создать свою первую песню 🎵
              </p>
              <Button variant="outline" onClick={() => { setSignupDone(false); setIsLogin(true); }}>
                Перейти к входу
              </Button>
            </div>
          ) : forgotSent ? (
            <div className="text-center space-y-4">
              <Mail className="w-12 h-12 text-primary mx-auto" />
              <p className="text-foreground font-medium">Проверьте вашу почту</p>
              <p className="text-muted-foreground text-sm">Мы отправили ссылку для сброса пароля на {email}</p>
              <Button variant="outline" onClick={() => { setForgotMode(false); setForgotSent(false); }}>
                Вернуться к входу
              </Button>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required className="pl-10" />
            </div>
            {!forgotMode && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type="password" placeholder="Пароль" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="pl-10" />
              </div>
            )}

            {isLogin && !forgotMode && (
              <div className="text-right">
                <button type="button" onClick={() => setForgotMode(true)} className="text-sm text-primary hover:underline">
                  Забыли пароль?
                </button>
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full h-12 text-base font-bold gap-2">
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-foreground" />
              ) : (
                <>
                  {forgotMode ? "Отправить ссылку" : isLogin ? "Войти" : "Зарегистрироваться"}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>

            {forgotMode && (
              <button type="button" onClick={() => setForgotMode(false)} className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors">
                Вернуться к входу
              </button>
            )}
          </form>
          )}

          {/* Toggle */}
          {!forgotMode && !forgotSent && !signupDone && (
            <div className="text-center mt-6">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {isLogin ? "Нет аккаунта? " : "Уже есть аккаунт? "}
                <span className="text-primary font-medium">
                  {isLogin ? "Зарегистрироваться" : "Войти"}
                </span>
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AuthPage;
